import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import BackMethods from "../../back_component/BackMethods";
import { can, PERMISSIONS } from "../../auth/permissions";
import {
    FaSearch, FaUserPlus, FaUsers,
    FaCheckCircle, FaTimesCircle, FaChevronLeft, FaChevronRight
} from "react-icons/fa";

const PatrolStaffManagementPage = () => {
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // --- حالات الترقيم الصفحي (Pagination States) ---
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalItems, setTotalItems] = useState(0);

    const navigate = useNavigate();
    const { backEnd_request } = BackMethods();

    const canCreate = can(PERMISSIONS.PATROL_STAFF_CREATE);
    const canEdit = can(PERMISSIONS.PATROL_STAFF_EDIT);
    const canDelete = can(PERMISSIONS.PATROL_STAFF_DELETE);

    // جلب الموظفين مع معاملات الترقيم
    const fetchStaff = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                size: pageSize,
            };
            const response = await backEnd_request.get("/PatrolStaff", { params });
            setStaff(response.data.data || []);
            setTotalItems(response.data.total || 0);
        } catch (error) {
            console.error("Error fetching Patrol Staff:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, backEnd_request]);

    useEffect(() => {
        fetchStaff();
    }, [fetchStaff]);

    // حساب عدد الصفحات الكلي
    const totalPages = Math.ceil(totalItems / pageSize);

    // البحث المحلي (في نتائج الصفحة الحالية)
    const filteredStaff = staff.filter(item =>
        `${item.firstName} ${item.fatherName} ${item.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.nationalId?.includes(searchTerm) ||
        item.departmentName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleDelete = async (id) => {
        if (!canDelete) return;
        if (!window.confirm("هل أنت متأكد من حذف الموظف؟")) return;
        try {
            await backEnd_request.delete(`/PatrolStaff/${id}`);
            fetchStaff();
        } catch (error) {
            console.error("Delete error:", error);
        }
    };

    const handleViewVisits = (item) => {
        const fullName = `${item.firstName} ${item.fatherName} ${item.lastName}`;
        navigate(`/admin/visits?staffName=${encodeURIComponent(fullName)}`);
    };

    const handleViewViolations = (item) => {
        const fullName = `${item.firstName} ${item.fatherName} ${item.lastName}`;
        navigate(`/admin/violations?inspector=${encodeURIComponent(fullName)}`);
    };

    return (
        <div className="container mt-4 pb-5" dir="rtl">
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-3 rounded-3 shadow-sm border-bottom border-primary border-3">
                <div>
                    <h3 className="fw-bold mb-0 text-dark">
                        <FaUsers className="ms-2 text-primary" /> إدارة الموظفين
                    </h3>
                    <p className="text-muted small mb-0 mt-1">عرض وتعديل بيانات المفتشين</p>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-primary px-4 shadow-sm d-flex align-items-center"
                        onClick={() => navigate("/admin/patrolstaff-management/create")}
                    >
                        <FaUserPlus className="ms-2" /> إضافة موظف جديد
                    </button>
                )}
            </div>

            {/* Stats & Search Bar */}
            <div className="row g-3 mb-4">
                <div className="col-md-6">
                    <div className="input-group shadow-sm">
                        <span className="input-group-text bg-white border-start-0">
                            <FaSearch className="text-muted" />
                        </span>
                        <input
                            type="text"
                            className="form-control border-end-0 py-2"
                            placeholder="ابحث بالاسم، القسم، أو الرقم الوطني..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="col-md-3">
                    <select
                        className="form-select shadow-sm py-2"
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    >
                        <option value={10}>10 موظفين / صفحة</option>
                        <option value={20}>20 موظف / صفحة</option>
                        <option value={50}>50 موظف / صفحة</option>
                    </select>
                </div>
                <div className="col-md-3">
                    <div className="bg-white p-2 px-3 rounded-3 shadow-sm d-flex justify-content-around align-items-center h-100">
                        <div className="text-center">
                            <div className="fw-bold text-primary">{totalItems}</div>
                            <div className="x-small text-muted">إجمالي</div>
                        </div>
                        <div className="vr"></div>
                        <div className="text-center">
                            <div className="fw-bold text-success">
                                {staff.filter(s => s.isActive).length}
                            </div>
                            <div className="x-small text-muted">بالصفحة</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div className="card border-0 shadow-sm rounded-3">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0 custom-staff-table">
                        <thead className="table-light">
                            <tr>
                                <th className="py-3">الموظف</th>
                                <th className="py-3">الرقم الوطني</th>
                                <th className="py-3">رقم الهاتف</th>
                                <th className="py-3">القسم</th>
                                <th className="py-3">الحالة</th>
                                <th className="py-3 text-center">العمليات</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border text-primary"></div></td></tr>
                            ) : filteredStaff.length > 0 ? (
                                filteredStaff.map((item) => (
                                    <tr key={item.patrolStaffId}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="avatar-circle ms-3">
                                                    {item.firstName?.[0] || 'U'}
                                                </div>
                                                <div>
                                                    <div className="fw-bold text-dark">{item.firstName} {item.lastName}</div>
                                                    <div className="text-muted small">الأب: {item.fatherName}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td><span className="badge bg-light text-dark border font-monospace">{item.nationalId}</span></td>
                                        <td>{item.phoneNumber}</td>
                                        <td><span className="text-primary fw-semibold">{item.departmentName}</span></td>
                                        <td>
                                            {item.isActive ? (
                                                <span className="badge bg-success-soft text-success">
                                                    <FaCheckCircle className="ms-1" /> نشط
                                                </span>
                                            ) : (
                                                <span className="badge bg-danger-soft text-danger">
                                                    <FaTimesCircle className="ms-1" /> غير فعال
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex justify-content-center gap-2">
                                                <button className="btn btn-light btn-sm shadow-sm" title="عرض الضبوط" onClick={() => handleViewViolations(item)}>
                                                    <i className="bi bi-file-earmark-text text-warning"></i>
                                                </button>
                                                <button className="btn btn-light btn-sm shadow-sm" title="سجل الزيارات" onClick={() => handleViewVisits(item)}>
                                                    <i className="bi bi-calendar-check text-info"></i>
                                                </button>
                                                {canEdit && (
                                                    <button className="btn btn-light btn-sm shadow-sm" title="تعديل" onClick={() => navigate(`/admin/patrolstaff-management/edit/${item.patrolStaffId}`)}>
                                                        <i className="bi bi-pencil-square text-primary"></i>
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button className="btn btn-light btn-sm shadow-sm" title="حذف" onClick={() => handleDelete(item.patrolStaffId)}>
                                                        <i className="bi bi-trash text-danger"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="6" className="text-center py-5 text-muted">لا توجد نتائج تطابق بحثك</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- نظام الترقيم الصفحي (Pagination Controls) --- */}
            {!loading && totalPages > 1 && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 bg-white p-3 rounded-3 shadow-sm gap-3">
                    <div className="text-muted small">
                        عرض الصفحة <b>{currentPage}</b> من أصل <b>{totalPages}</b>
                    </div>
                    <nav>
                        <ul className="pagination mb-0 gap-1">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link rounded-circle border-0 shadow-sm" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                                    <FaChevronRight size={12} />
                                </button>
                            </li>

                            {[...Array(totalPages)].map((_, index) => {
                                const pageNum = index + 1;
                                if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                                    return (
                                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                            <button className="page-link rounded-pill border-0 mx-1 shadow-sm" onClick={() => setCurrentPage(pageNum)}>
                                                {pageNum}
                                            </button>
                                        </li>
                                    );
                                } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                    return <li key={pageNum} className="px-1 align-self-end text-muted">...</li>;
                                }
                                return null;
                            })}

                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button className="page-link rounded-circle border-0 shadow-sm" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                                    <FaChevronLeft size={12} />
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            )}

            <style>{`
                .x-small { font-size: 0.7rem; }
                .bg-success-soft { background-color: #e8f5e9; }
                .bg-danger-soft { background-color: #ffebee; }
                .avatar-circle { width: 40px; height: 40px; background-color: #e3f2fd; color: #0d6efd; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 1.1rem; }
                .custom-staff-table thead th { font-size: 0.85rem; font-weight: 700; color: #555; border-top: none; }
                .custom-staff-table tbody tr:hover { background-color: #f8faff !important; }
                .btn-light { background-color: #fff; border: 1px solid #eee; }
                .font-monospace { letter-spacing: 1px; }
                .page-link { color: #0d6efd; }
                .page-item.active .page-link { background-color: #0d6efd; color: white; }
            `}</style>
        </div>
    );
};

export default PatrolStaffManagementPage;
